import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import axiosClient from "../../api/axiosClient";
import DataTable from "../brand/DataTable";

export default function OwnerPortalAccess() {
  const [data, setData] = useState({ invites: [], assignments: [] });
  const [toast, setToast] = useState("");
  const [revoke, setRevoke] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [orphanTotal, setOrphanTotal] = useState(0);
  const [orphanPage, setOrphanPage] = useState(0);

  const load = () => {
    axiosClient.get("/client-access/").then((response) => setData(response.data));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    axiosClient
      .get("/client-access/orphans/", { params: { page: orphanPage + 1, pageSize: 10 } })
      .then((response) => {
        setOrphans(response.data.results || []);
        setOrphanTotal(response.data.total || 0);
      });
  }, [orphanPage]);

  const resend = async (inviteId) => {
    await axiosClient.post(`/client-invites/${inviteId}/resend/`);
    setToast("Invite resent.");
  };

  const confirmRevoke = async () => {
    await axiosClient.post(`/users/${revoke.user_id}/client-revoke/`, { unit_id: revoke.unit_id });
    setRevoke(null);
    setToast("Unit access removed.");
    load();
  };

  return (
    <Stack spacing={2} sx={{ mt: 4 }}>
      <Typography variant="h6">Owner portal invites</Typography>
      {data.invites.length === 0 && <Typography>No open invites.</Typography>}
      {data.invites.map((invite) => (
        <Stack key={invite.id} direction="row" spacing={2} alignItems="center">
          <Typography sx={{ flex: 1 }}>{invite.email} · {(invite.units || []).join(", ")}</Typography>
          <Button variant="outlined" onClick={() => resend(invite.id)}>+ Resend</Button>
        </Stack>
      ))}
      <Typography variant="h6">Unit access</Typography>
      {data.assignments.map((row) => (
        <Stack key={`${row.user_id}-${row.unit_id}`} direction="row" spacing={2} alignItems="center">
          <Typography sx={{ flex: 1 }}>{row.email} · {row.unit_name}</Typography>
          <Button variant="outlined" color="error" startIcon={<RemoveIcon />} onClick={() => setRevoke(row)}>
            Revoke
          </Button>
        </Stack>
      ))}
      <Typography variant="h6">Orphaned portal users</Typography>
      <Typography variant="body2" color="text.secondary">
        These people can still sign in, and they have no units. Remedial steps are not available yet.
      </Typography>
      <DataTable size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orphans.length === 0 && (
            <TableRow>
              <TableCell colSpan={2}>No orphaned portal users.</TableCell>
            </TableRow>
          )}
          {orphans.map((row) => (
            <TableRow key={row.user_id}>
              <TableCell>{row.name || "—"}</TableCell>
              <TableCell>{row.email}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </DataTable>
      <TablePagination
        component="div"
        count={orphanTotal}
        page={orphanPage}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        onPageChange={(_, value) => setOrphanPage(value)}
      />
      <Dialog open={Boolean(revoke)} onClose={() => setRevoke(null)}>
        <DialogTitle>Remove portal access?</DialogTitle>
        <DialogContent>
          <Typography>
            {revoke ? `${revoke.email} will no longer see ${revoke.unit_name}.` : ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoke(null)}>Cancel</Button>
          <Button variant="outlined" color="error" onClick={confirmRevoke}>Revoke access</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(toast)} message={toast} autoHideDuration={3000} onClose={() => setToast("")} />
    </Stack>
  );
}
